import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import RequestFormFieldsTable from "../tables/RequestFormFieldsTable";
import {
  buildTestTypeAlignmentWarnings,
  defaultTestTypeIdsForModes,
  ONBOARD_OPERATING_MODES,
  onboardSelectionsFromStarter,
  type OnboardCttSelection,
  type OnboardTestTypeOption,
} from "@/utils/onboardHelpers";
import { isDepartmentRequestField } from "@/utils/formSelectionOptions";
import { apiMessage } from "@/utils/apiMessage";

type BillingOrgOption = { id: number; name: string };
type AccountManagerOption = {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
};

type OnboardContext = {
  templateFound?: boolean;
  bootstrapMode?: boolean;
  templateCompanyId?: number;
  templateCompanyName?: string;
  billingOrgs?: BillingOrgOption[];
  accountManagerCandidates?: AccountManagerOption[];
  testTypes?: OnboardTestTypeOption[];
  copySources?: CopySourceItem[];
};

type TestTypeField = {
  id: number;
  sequence: number;
  required: boolean;
  proctorProvided: boolean;
  label: string;
  testRequestFieldId: number;
  testRequestFieldName?: string;
};

type PlatformTestTypeItem = {
  id: number;
  name: string;
  active?: boolean;
  fields?: TestTypeField[];
};

type FieldRow = {
  id: number;
  fieldId: number;
  name: string;
  required: boolean;
  proctorProvided: boolean;
  testRequestFieldName?: string;
};

type CopySourceItem = {
  id: number;
  companyId: number;
  companyName: string;
  name: string;
  testTypeName: string;
};

type PendingNewTestType = {
  key: string;
  testTypeId: number;
  platformTestTypeName: string;
  name: string;
  fields: Array<{ fieldId: number; name: string; required: boolean; proctorProvided: boolean }>;
};

type SelectedTestTypeRow = {
  key: string;
  source: string;
  companyName: string;
  name: string;
  testTypeName: string;
  kind: "starter" | "copy" | "create";
  starterId?: number;
  copiedId?: number;
  pendingKey?: string;
};

type TestTypeStepMode = "starter" | "copy" | "create";

export type AddCustomerWizardModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  onCreated?: (companyId: number) => void;
  updateSpinner?: (loading: boolean) => void;
};

function defaultBillingOrgId(orgs: BillingOrgOption[]): number {
  const rightTime = orgs.find(
    (o) => o.name.localeCompare("RightTime", undefined, { sensitivity: "accent" }) === 0
  );
  if (rightTime) return rightTime.id;
  if (orgs.length === 1) return orgs[0].id;
  return 0;
}

function parseCopySourcesResponse(
  jsonData: unknown,
  templateCompanyId?: number
): CopySourceItem[] {
  if (!jsonData || typeof jsonData !== "object") return [];
  const data = jsonData as Record<string, unknown>;
  const raw = data.companyTestTypes ?? data.copySources ?? data.CopySources;
  if (!Array.isArray(raw)) return [];
  const list = raw as CopySourceItem[];
  return templateCompanyId
    ? list.filter((s) => s.companyId !== templateCompanyId)
    : list;
}

const WIZARD_STEPS = [
  { id: "identity", label: "Company identity" },
  { id: "billing", label: "Billing org" },
  { id: "managers", label: "Account managers" },
  { id: "modes", label: "Operating mode" },
  { id: "testTypes", label: "Test types" },
] as const;

type WizardStepId = (typeof WIZARD_STEPS)[number]["id"];

function isBootstrapMode(context: OnboardContext | null): boolean {
  if (!context)
    return false;
  if (context.bootstrapMode === true)
    return true;
  if (context.templateFound === true)
    return false;
  return !context.templateCompanyId;
}

function wizardStepsForTemplate(bootstrapMode: boolean) {
  if (bootstrapMode)
    return WIZARD_STEPS.filter((item) => item.id !== "modes");
  return WIZARD_STEPS;
}

function wizardStepCardClass(index: number, currentStep: number, isComplete: boolean): string {
  if (index === currentStep) return "workflowNode isActive";
  if (isComplete) return "workflowNode isComplete";
  return "workflowNode isFuture";
}

export default function AddCustomerWizardModal({
  isOpen,
  onClose,
  onCreated,
  updateSpinner,
}: AddCustomerWizardModalProps) {
  const [step, setStep] = useState(0);
  const [context, setContext] = useState<OnboardContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [alias, setAlias] = useState("");
  const [billingOrgId, setBillingOrgId] = useState(0);
  const [accountManagerIds, setAccountManagerIds] = useState<number[]>([]);
  const [operatingModes, setOperatingModes] = useState<string[]>([]);
  const [starterCttIds, setStarterCttIds] = useState<number[]>([]);
  const [copiedCttIds, setCopiedCttIds] = useState<number[]>([]);
  const [selectedCopySourceIds, setSelectedCopySourceIds] = useState<number[]>([]);
  const [pendingNewTestTypes, setPendingNewTestTypes] = useState<PendingNewTestType[]>([]);
  const [testTypesInitialized, setTestTypesInitialized] = useState(false);

  const [testTypeStepMode, setTestTypeStepMode] = useState<TestTypeStepMode>("starter");
  const [copySources, setCopySources] = useState<CopySourceItem[]>([]);
  const [platformTestTypes, setPlatformTestTypes] = useState<PlatformTestTypeItem[]>([]);
  const [selectedPlatformTestTypeId, setSelectedPlatformTestTypeId] = useState("");
  const [newCttName, setNewCttName] = useState("");
  const [newCttSeedFields, setNewCttSeedFields] = useState<FieldRow[]>([]);
  const currentNewFieldsRef = useRef<FieldRow[]>([]);

  const starterTestTypes = useMemo(() => context?.testTypes ?? [], [context?.testTypes]);
  const bootstrapMode = isBootstrapMode(context);
  const wizardSteps = useMemo(
    () => (context ? wizardStepsForTemplate(bootstrapMode) : []),
    [context, bootstrapMode]
  );
  const lastStepIndex = Math.max(wizardSteps.length - 1, 0);
  const activeStep = Math.min(step, lastStepIndex);
  const currentStepId = wizardSteps[activeStep]?.id;

  const resetForm = useCallback(() => {
    setStep(0);
    setName("");
    setAlias("");
    setBillingOrgId(0);
    setAccountManagerIds([]);
    setOperatingModes([]);
    setStarterCttIds([]);
    setCopiedCttIds([]);
    setSelectedCopySourceIds([]);
    setPendingNewTestTypes([]);
    setTestTypesInitialized(false);
    setTestTypeStepMode("starter");
    setCopySources([]);
    setPlatformTestTypes([]);
    setSelectedPlatformTestTypeId("");
    setNewCttName("");
    setNewCttSeedFields([]);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
    setLoadingContext(true);
    updateSpinner?.(true);

    async function loadWizardContextAsync() {
      try {
        const [contextResp, copyResp] = await Promise.all([
          RightTimeApi.getAsync("/Company/Onboard/Context"),
          RightTimeApi.getAsync("/CompanyTestTypes/copySources"),
        ]);

        if (contextResp?.ok && contextResp.jsonData) {
          const data = contextResp.jsonData as OnboardContext;
          setContext(data);
          const orgs = data.billingOrgs ?? [];
          setBillingOrgId(defaultBillingOrgId(orgs));

          const templateId = data.templateCompanyId;
          // Prefer dedicated copySources endpoint; Context no longer embeds the catalog.
          const copyList = copyResp?.ok
            ? parseCopySourcesResponse(copyResp.jsonData, templateId)
            : [];
          setCopySources(copyList);
        } else {
          toast.error("Failed to load onboarding data.");
          if (copyResp?.ok) {
            setCopySources(parseCopySourcesResponse(copyResp.jsonData));
          }
        }
      } catch {
        toast.error("Failed to load onboarding data.");
      } finally {
        setLoadingContext(false);
        updateSpinner?.(false);
      }
    }

    loadWizardContextAsync();
  }, [isOpen, resetForm, updateSpinner]);

  useEffect(() => {
    if (context && bootstrapMode)
      setTestTypeStepMode("copy");
  }, [context, bootstrapMode]);

  useEffect(() => {
    if (step !== activeStep)
      setStep(activeStep);
  }, [step, activeStep]);

  useEffect(() => {
    if (currentStepId !== "testTypes" || testTypesInitialized || starterTestTypes.length === 0)
      return;
    setStarterCttIds(defaultTestTypeIdsForModes(starterTestTypes, operatingModes));
    setTestTypesInitialized(true);
  }, [currentStepId, operatingModes, starterTestTypes, testTypesInitialized]);

  useEffect(() => {
    if (currentStepId === "testTypes")
      setTestTypesInitialized(false);
  }, [operatingModes, currentStepId]);

  useEffect(() => {
    if (!isOpen || currentStepId !== "testTypes" || testTypeStepMode !== "create") return;

    let cancelled = false;

    async function fetchPlatformTestTypes() {
      const resp = await RightTimeApi.getAsync("/TestTypes");
      if (cancelled) return;
      if (resp?.ok && resp.jsonData?.testTypes && Array.isArray(resp.jsonData.testTypes)) {
        const list = (resp.jsonData.testTypes as PlatformTestTypeItem[])
          .filter((tt) => tt.active)
          .sort((a, b) => a.name.localeCompare(b.name));
        setPlatformTestTypes(list);
      } else {
        setPlatformTestTypes([]);
      }
    }

    fetchPlatformTestTypes();
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentStepId, testTypeStepMode]);

  const totalTestTypeCount =
    starterCttIds.length + copiedCttIds.length + pendingNewTestTypes.length;

  const alignmentSelections = useMemo((): OnboardCttSelection[] => {
    const selections = onboardSelectionsFromStarter(starterTestTypes, starterCttIds);
    for (const id of copiedCttIds) {
      const source = copySources.find((s) => s.id === id);
      if (source)
        selections.push({ name: source.name, testTypeName: source.testTypeName });
    }
    for (const pending of pendingNewTestTypes) {
      selections.push({ name: pending.name, testTypeName: pending.platformTestTypeName });
    }
    return selections;
  }, [starterTestTypes, starterCttIds, copiedCttIds, copySources, pendingNewTestTypes]);

  const stableTestTypeFields = useMemo(() =>
    newCttSeedFields.map((r) => ({
      id: r.fieldId,
      testRequestFieldId: r.fieldId,
      label: r.name,
      required: r.required,
      proctorProvided: r.proctorProvided,
      testRequestFieldName: r.testRequestFieldName,
    })), [newCttSeedFields]);

  const stableFieldTypes = useMemo(() =>
    newCttSeedFields.map((r) => ({ id: r.fieldId, name: r.testRequestFieldName || r.name })),
    [newCttSeedFields]);

  const stepCompletion = useMemo(() => ({
    identity: name.trim().length > 0,
    billing: billingOrgId > 0,
    managers: accountManagerIds.length > 0,
    modes: operatingModes.length > 0,
    testTypes: totalTestTypeCount > 0,
  }), [name, billingOrgId, accountManagerIds, operatingModes, totalTestTypeCount]);

  const isStepComplete = useCallback((index: number) => {
    const stepId = wizardSteps[index]?.id as WizardStepId | undefined;
    switch (stepId) {
      case "identity": return stepCompletion.identity;
      case "billing": return stepCompletion.billing;
      case "managers": return stepCompletion.managers;
      case "modes": return stepCompletion.modes;
      case "testTypes": return bootstrapMode ? true : stepCompletion.testTypes;
      default: return false;
    }
  }, [wizardSteps, stepCompletion, bootstrapMode]);

  const canGoNext = useMemo(() => isStepComplete(activeStep), [isStepComplete, activeStep]);

  const canCreateCustomer = useMemo(() => {
    if (bootstrapMode)
      return stepCompletion.identity && stepCompletion.billing && stepCompletion.managers;
    return stepCompletion.identity
      && stepCompletion.billing
      && stepCompletion.managers
      && stepCompletion.modes
      && stepCompletion.testTypes;
  }, [stepCompletion, bootstrapMode]);

  const isFinalStep = activeStep === lastStepIndex;

  function toggleId(list: number[], id: number): number[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  function toggleMode(modeId: string) {
    setOperatingModes((prev) =>
      prev.includes(modeId) ? prev.filter((m) => m !== modeId) : [...prev, modeId]
    );
  }

  function fieldRowsFromPlatform(platform?: PlatformTestTypeItem): FieldRow[] {
    return (platform?.fields ?? [])
      .filter((f) => f.testRequestFieldId > 0)
      .filter((f) => !isDepartmentRequestField({
        fieldId: f.testRequestFieldId,
        fieldElementName: f.testRequestFieldName,
      }))
      .sort((a, b) => a.sequence - b.sequence)
      .map((f) => ({
        id: f.testRequestFieldId,
        fieldId: f.testRequestFieldId,
        name: f.label,
        required: f.required,
        proctorProvided: f.proctorProvided,
        testRequestFieldName: f.testRequestFieldName,
      }));
  }

  function resolvePendingNewTestTypeFields(platform: PlatformTestTypeItem): FieldRow[] {
    const platformRows = fieldRowsFromPlatform(platform);
    if (platformRows.length > 0)
      return platformRows;
    return currentNewFieldsRef.current;
  }

  function handlePlatformTestTypeChange(e: ChangeEvent<HTMLSelectElement>) {
    const nextId = e.target.value;
    setSelectedPlatformTestTypeId(nextId);

    const selected = platformTestTypes.find((tt) => String(tt.id) === nextId);
    setNewCttName(selected?.name || "");

    const rows = fieldRowsFromPlatform(selected);
    setNewCttSeedFields(rows);
    currentNewFieldsRef.current = rows;
  }

  function handleNewCttFieldsChange(nextRows: Array<{
    id: number;
    testRequestFieldId: number;
    testRequestFieldName?: string;
    label: string;
    required: boolean;
    proctorProvided: boolean;
  }>) {
    currentNewFieldsRef.current = nextRows.map((row) => ({
      id: row.id,
      fieldId: row.testRequestFieldId,
      name: row.label,
      required: !!row.required,
      proctorProvided: !!row.proctorProvided,
      testRequestFieldName: row.testRequestFieldName,
    }));
  }

  function addPendingNewTestType() {
    if (!selectedPlatformTestTypeId) {
      toast.error("Please select a test type.");
      return;
    }
    if (!newCttName.trim()) {
      toast.error("Please enter a name.");
      return;
    }

    const platform = platformTestTypes.find((tt) => String(tt.id) === selectedPlatformTestTypeId);
    if (!platform) return;

    const resolvedFields = resolvePendingNewTestTypeFields(platform);

    setPendingNewTestTypes((prev) => [
      ...prev,
      {
        key: `new-${platform.id}-${Date.now()}`,
        testTypeId: platform.id,
        platformTestTypeName: platform.name,
        name: newCttName.trim(),
        fields: resolvedFields.map((r) => ({
          fieldId: r.fieldId,
          name: r.name,
          required: r.required,
          proctorProvided: r.proctorProvided,
        })),
      },
    ]);

    setSelectedPlatformTestTypeId("");
    setNewCttName("");
    setNewCttSeedFields([]);
    currentNewFieldsRef.current = [];
  }

  function removePendingNewTestType(key: string) {
    setPendingNewTestTypes((prev) => prev.filter((p) => p.key !== key));
  }

  function addSelectedCopySources() {
    if (selectedCopySourceIds.length === 0) return;
    setCopiedCttIds((prev) => {
      const next = new Set([...prev, ...selectedCopySourceIds]);
      return Array.from(next);
    });
    setSelectedCopySourceIds([]);
  }

  function removeCopiedCtt(id: number) {
    setCopiedCttIds((prev) => prev.filter((x) => x !== id));
    setSelectedCopySourceIds((prev) => prev.filter((x) => x !== id));
  }

  const copiedCttItems = useMemo(
    () => copiedCttIds
      .map((id) => copySources.find((s) => s.id === id))
      .filter((item): item is CopySourceItem => !!item),
    [copiedCttIds, copySources]
  );

  function removeStarterCtt(id: number) {
    setStarterCttIds((prev) => prev.filter((x) => x !== id));
  }

  function removeSelectedTestType(row: SelectedTestTypeRow) {
    if (row.kind === "starter" && row.starterId != null)
      removeStarterCtt(row.starterId);
    else if (row.kind === "copy" && row.copiedId != null)
      removeCopiedCtt(row.copiedId);
    else if (row.kind === "create" && row.pendingKey)
      removePendingNewTestType(row.pendingKey);
  }

  async function submitAsync() {
    if (!canCreateCustomer) {
      toast.error("Complete all wizard steps before creating the customer.");
      return;
    }

    if (!bootstrapMode) {
      const warnings = buildTestTypeAlignmentWarnings(alignmentSelections, operatingModes);
      for (const w of warnings)
        toast.warn(w);
    }

    setSubmitting(true);
    updateSpinner?.(true);
    const payload = {
      name: name.trim(),
      alias: alias.trim() || null,
      billingOrgId,
      operatingModes: bootstrapMode ? [] : operatingModes,
      testTypeIds: starterCttIds,
      copiedCttIds,
      newTestTypes: pendingNewTestTypes.map((p) => ({
        testTypeId: p.testTypeId,
        name: p.name,
        fields: p.fields,
      })),
      accountManagerUserIds: accountManagerIds,
    };
    const resp = await RightTimeApi.postAsync("/Company/Onboard", payload);
    setSubmitting(false);
    updateSpinner?.(false);

    if (!resp?.ok) {
      toast.error(apiMessage(resp, resp?.statusText ?? "Failed to create customer."));
      return;
    }

    const companyId = resp.jsonData?.companyId;
    const serverWarnings: string[] = resp.jsonData?.warnings ?? [];
    for (const w of serverWarnings)
      toast.warn(w);

    const displayName = alias.trim() || name.trim();
    toast.success(`Customer "${displayName}" created.`);
    onClose?.();
    if (companyId)
      onCreated?.(companyId);
  }

  function handleNext() {
    if (!isFinalStep) {
      if (wizardSteps[activeStep]?.id === "modes")
        setTestTypesInitialized(false);
      setStep(activeStep + 1);
      return;
    }
    submitAsync();
  }

  function handleBack() {
    if (activeStep > 0)
      setStep(activeStep - 1);
  }

  function handleStepCardClick(index: number) {
    if (submitting || loadingContext || !context || index === activeStep)
      return;
    if (index > activeStep) {
      for (let i = activeStep; i < index; i++) {
        if (!isStepComplete(i))
          return;
      }
    }
    setStep(index);
  }

  function getStepSummary(stepId: WizardStepId | undefined): string | null {
    switch (stepId) {
      case "identity":
        return name.trim() ? (alias.trim() || name.trim()) : null;
      case "billing": {
        const org = context?.billingOrgs?.find((o) => o.id === billingOrgId);
        return org?.name ?? null;
      }
      case "managers":
        return accountManagerIds.length > 0
          ? `${accountManagerIds.length} selected`
          : null;
      case "modes":
        return operatingModes.length > 0
          ? `${operatingModes.length} mode${operatingModes.length === 1 ? "" : "s"}`
          : null;
      case "testTypes":
        return totalTestTypeCount > 0
          ? `${totalTestTypeCount} test type${totalTestTypeCount === 1 ? "" : "s"}`
          : null;
      default:
        return null;
    }
  }

  const templateName = context?.templateCompanyName ?? "zz_Starter";

  const selectedTestTypeRows = useMemo((): SelectedTestTypeRow[] => {
    const rows: SelectedTestTypeRow[] = [];

    for (const id of starterCttIds) {
      const tt = starterTestTypes.find((t) => t.id === id);
      if (!tt) continue;
      rows.push({
        key: `starter-${id}`,
        source: templateName,
        companyName: templateName,
        name: tt.name,
        testTypeName: tt.testTypeName,
        kind: "starter",
        starterId: id,
      });
    }

    for (const item of copiedCttItems) {
      rows.push({
        key: `copy-${item.id}`,
        source: "Copy",
        companyName: item.companyName,
        name: item.name,
        testTypeName: item.testTypeName,
        kind: "copy",
        copiedId: item.id,
      });
    }

    for (const pending of pendingNewTestTypes) {
      rows.push({
        key: pending.key,
        source: "Create",
        companyName: "—",
        name: pending.name,
        testTypeName: pending.platformTestTypeName,
        kind: "create",
        pendingKey: pending.key,
      });
    }

    return rows;
  }, [starterCttIds, starterTestTypes, copiedCttItems, pendingNewTestTypes, templateName]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Customer"
      className="addCustomerWizardModal"
      size="xl"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {activeStep > 0 && (
            <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={submitting}>
              Back
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary OkButton"
            onClick={handleNext}
            disabled={
              (isFinalStep ? !canCreateCustomer : !canGoNext) ||
              submitting ||
              loadingContext ||
              !context
            }
          >
            {isFinalStep ? "Create Customer" : "Next"}
          </button>
        </>
      )}
    >
      <div className="cultureWorkflow wizardWorkflow">
        <div className="workflowTimeline" role="list" aria-label="Onboarding steps">
          <div className="workflowTrack" aria-hidden="true" />
          <div className="workflowItems">
            {wizardSteps.map((item, index) => {
              const summary = getStepSummary(item.id);
              const canNavigate = !submitting && !loadingContext && !!context;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="listitem"
                  className={wizardStepCardClass(index, activeStep, isStepComplete(index))}
                  aria-current={index === activeStep ? "step" : undefined}
                  disabled={!canNavigate}
                  onClick={() => handleStepCardClick(index)}
                >
                  <div className="workflowStep">Step {index + 1}</div>
                  <div className="workflowLabel">{item.label}</div>
                  {summary && (
                    <div className="workflowKpi">
                      <div className="workflowKpiSummary">{summary}</div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="wizardDetails">
      {loadingContext && <p>Loading…</p>}

      {bootstrapMode && (
        <p className="templateMissingNotice">
          Template company &quot;{templateName}&quot; was not found. Operating modes and starter test types are skipped.
          Create &quot;{templateName}&quot; here, then configure roles and CTTs before onboarding other customers.
        </p>
      )}

      {!loadingContext && currentStepId === "identity" && (
        <>
          <div className="wizardField">
            <label htmlFor="onboard-company-name">Company name</label>
            <input
              id="onboard-company-name"
              name="CompanyName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="wizardField">
            <label htmlFor="onboard-company-alias">Alias (optional)</label>
            <input
              id="onboard-company-alias"
              name="Alias"
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
            />
          </div>
        </>
      )}

      {!loadingContext && currentStepId === "billing" && (
        <div className="wizardField">
          <label htmlFor="onboard-billing-org">Billing org</label>
          <select
            id="onboard-billing-org"
            name="BillingOrgId"
            value={billingOrgId || ""}
            onChange={(e) => setBillingOrgId(parseInt(e.target.value, 10) || 0)}
          >
            <option value="">Select billing org…</option>
            {(context?.billingOrgs ?? []).map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
      )}

      {!loadingContext && currentStepId === "managers" && (
        <div className="tableWrapper factTable scrollable">
          <table className="wizardCheckboxTable">
            <caption>RightTime account managers</caption>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th className="actions">Select</th>
              </tr>
            </thead>
            <tbody>
              {(context?.accountManagerCandidates ?? []).map((user) => (
                <tr
                  key={user.userId}
                  onClick={() => setAccountManagerIds((prev) => toggleId(prev, user.userId))}
                >
                  <td>{user.firstName} {user.lastName}</td>
                  <td>{user.email}</td>
                  <td className="actions">
                    {accountManagerIds.includes(user.userId) ? (
                      <MdCheckBox className="checkbox checked actionIcon" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loadingContext && currentStepId === "modes" && (
        <ul className="wizardModeList">
          {ONBOARD_OPERATING_MODES.map((mode) => (
            <li key={mode.id} data-mode-id={mode.id} onClick={() => toggleMode(mode.id)}>
              {operatingModes.includes(mode.id) ? (
                <MdCheckBox className="checkbox checked actionIcon" />
              ) : (
                <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
              )}
              {" "}
              <strong>{mode.label}</strong>
              <span className="modeDescription">{mode.description}</span>
            </li>
          ))}
        </ul>
      )}

      {!loadingContext && currentStepId === "testTypes" && (
        <div className="wizardTestTypesStep">
          <div className="addTestTypeModeToggle">
            {!bootstrapMode && (
            <button
              type="button"
              className={`addTestTypeModeButton${testTypeStepMode === "starter" ? " active" : ""}`}
              onClick={() => setTestTypeStepMode("starter")}
            >
              From {templateName}
            </button>
            )}
            <button
              type="button"
              className={`addTestTypeModeButton${testTypeStepMode === "copy" ? " active" : ""}`}
              onClick={() => setTestTypeStepMode("copy")}
            >
              Copy
            </button>
            <button
              type="button"
              className={`addTestTypeModeButton${testTypeStepMode === "create" ? " active" : ""}`}
              onClick={() => setTestTypeStepMode("create")}
            >
              Create
            </button>
          </div>

          {testTypeStepMode === "starter" && (
            <div className="tableWrapper dimensionTable scrollable">
              <table className="wizardCheckboxTable testTypesTable">
                <caption>Test types from {templateName}</caption>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Platform type</th>
                    <th className="actions">Select</th>
                  </tr>
                </thead>
                <tbody>
                  {starterTestTypes.map((tt) => (
                    <tr
                      key={tt.id}
                      data-test-type={tt.testTypeName}
                      onClick={() => setStarterCttIds((prev) => toggleId(prev, tt.id))}
                    >
                      <td>{tt.name}</td>
                      <td>{tt.testTypeName}</td>
                      <td className="actions">
                        {starterCttIds.includes(tt.id) ? (
                          <MdCheckBox className="checkbox checked actionIcon" />
                        ) : (
                          <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {testTypeStepMode === "copy" && (
            <div className="wizardCopyTab">
              {copySources.length === 0 ? (
                <p className="addTestTypeCopyStatus">No active company test types available to copy.</p>
              ) : (
                <div className="wizardCopySourceList">
                  <table className="addTestTypeCopyTable wizardCheckboxTable">
                    <thead>
                      <tr>
                        <th className="actions">Select</th>
                        <th>Company</th>
                        <th>CTT Name</th>
                        <th>Test Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {copySources.map((source) => {
                        const alreadyCopied = copiedCttIds.includes(source.id);
                        const isSelected = selectedCopySourceIds.includes(source.id);
                        return (
                        <tr
                          key={source.id}
                          className={isSelected || alreadyCopied ? "selected" : ""}
                          onClick={() => {
                            if (alreadyCopied) return;
                            setSelectedCopySourceIds((prev) => toggleId(prev, source.id));
                          }}
                        >
                          <td className="actions">
                            {alreadyCopied || isSelected ? (
                              <MdCheckBox className={`checkbox checked actionIcon${alreadyCopied ? " disabled" : ""}`} />
                            ) : (
                              <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                            )}
                          </td>
                          <td className="left">{source.companyName}</td>
                          <td className="left">{source.name}</td>
                          <td className="left">{source.testTypeName}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="wizardCopyTestTypesActions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addSelectedCopySources}
                  disabled={selectedCopySourceIds.length === 0}
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {testTypeStepMode === "create" && (
            <div className="wizardCreateTestTypes">
              <div className="addTestTypeFieldRow">
                <label htmlFor="onboard-test-type-select" className="addTestTypeLabel">Test Type:</label>
                <select
                  id="onboard-test-type-select"
                  className="testTypeSelect addTestTypeSelect"
                  value={selectedPlatformTestTypeId}
                  onChange={handlePlatformTestTypeChange}
                >
                  <option value="">-- Select --</option>
                  {platformTestTypes.map((tt) => (
                    <option key={tt.id} value={tt.id}>{tt.name}</option>
                  ))}
                </select>
              </div>
              <div className="addTestTypeFieldRow">
                <label htmlFor="onboard-new-ctt-name" className="addTestTypeLabel">Name:</label>
                <input
                  id="onboard-new-ctt-name"
                  className="nameInput addTestTypeNameInput"
                  type="text"
                  value={newCttName}
                  onChange={(e) => setNewCttName(e.target.value)}
                  placeholder="Enter name"
                />
              </div>
              {newCttSeedFields.length > 0 && (
                <RequestFormFieldsTable
                  mode="testTypeDraft"
                  fieldTypes={stableFieldTypes}
                  testTypeFields={stableTestTypeFields}
                  onTestTypeFieldsChange={handleNewCttFieldsChange}
                  departmentsEnabled={false}
                />
              )}
              <div className="wizardCreateTestTypesActions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addPendingNewTestType}
                  disabled={!selectedPlatformTestTypeId || !newCttName.trim()}
                >
                  Add to list
                </button>
              </div>
            </div>
          )}

          {selectedTestTypeRows.length > 0 && (
          <div className="tableWrapper dimensionTable scrollable wizardSelectedTestTypesList">
            <table className="wizardCheckboxTable">
              <caption>Test types for new customer</caption>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Company</th>
                  <th>CTT Name</th>
                  <th>Test Type</th>
                  <th className="actions">Remove</th>
                </tr>
              </thead>
              <tbody>
                {selectedTestTypeRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.source}</td>
                    <td>{row.companyName}</td>
                    <td>{row.name}</td>
                    <td>{row.testTypeName}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => removeSelectedTestType(row)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}
        </div>
      </div>
    </Modal>
  );
}
